// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Where an inspector value came from — the tag beside each control (v8
 * "Felter der forklarer sig selv"): fra scanningen · NN %, målt i modellen,
 * fundet i scanningen, fordelt ligeligt, foreslået, beregnet, indtastet,
 * mangler.
 *
 * Contract gap 2 is closed by ReUseX schema v37: a type and a part carry
 * `provenance` (kind scan / measured / calculated / entered / suggested,
 * per field), and the type's tonnes say themselves where they came from
 * (`mass_source`, `mass_method`). Those win wherever they are sent. A server
 * before it sends neither; then the tags are heuristics over what the survey
 * does say (instance links, the type's AI confidence, the parts' quantities,
 * the chosen build-up). Where nothing can honestly be claimed the tag is left
 * out (null) rather than guessed. Pure: no React, no DOM.
 */

import type { FieldProvenance, SurveyPart, SurveyType } from '../api/types';
import { formatNumber } from '../kortlaegning/vocab';
import { roundTenth, type Buildup } from './buildups';

export type ProvenanceKind = 'scan' | 'measured' | 'found' | 'split' | 'suggested' | 'calculated' | 'entered' | 'missing';

export interface Provenance {
  kind: ProvenanceKind;
  label: string;
  /** Drawn in the warn tone (a value the report still needs). */
  warn: boolean;
}

const tag = (kind: ProvenanceKind, label: string, warn = false): Provenance => ({ kind, label, warn });

export const MISSING: Provenance = tag('missing', 'mangler', true);
export const ENTERED: Provenance = tag('entered', 'indtastet');
export const FOUND: Provenance = tag('found', 'fundet i scanningen');
export const SPLIT: Provenance = tag('split', 'fordelt ligeligt');
export const SUGGESTED: Provenance = tag('suggested', 'foreslået');
export const CALCULATED: Provenance = tag('calculated', 'beregnet');
export const MEASURED: Provenance = tag('measured', 'målt i modellen');

/** The tag of one server provenance entry (`SurveyType.provenance[field]`), or null without one. */
export function serverTag(p: FieldProvenance | null | undefined): Provenance | null {
  if (!p) return null;
  switch (p.kind) {
    case 'scan':
      return tag('scan', p.confidence !== null ? `fra scanningen · ${Math.round(p.confidence * 100)} %` : 'fra scanningen');
    case 'measured':
      return MEASURED;
    case 'calculated':
      return CALCULATED;
    case 'suggested':
      return SUGGESTED;
    case 'entered':
      return ENTERED;
    default:
      return null;
  }
}

/** The server records provenance for this type (ReUseX schema v37); heuristics only when it does not. */
export function hasServerProvenance(type: Pick<SurveyType, 'provenance'>): boolean {
  return type.provenance !== undefined;
}

/**
 * The parts' common provenance for a field: the tag when every part that
 * carries one agrees (`målt i modellen` for a type whose parts were all
 * measured), null when they disagree or none says.
 */
export function partsTag(parts: readonly Pick<SurveyPart, 'provenance'>[], field: string): Provenance | null {
  const kinds = new Set<string>();
  let first: FieldProvenance | null = null;
  for (const p of parts) {
    const e = p.provenance?.[field];
    if (!e) continue;
    kinds.add(e.kind);
    first ??= e;
  }
  return kinds.size === 1 ? serverTag(first) : null;
}

/** A part the scan produced: it names an instance. */
export function isScanPart(p: Pick<SurveyPart, 'instance_guid' | 'instance_id'>): boolean {
  return p.instance_guid !== null || (p.instance_id !== null && p.instance_id >= 1);
}

/** A type with at least one part from the scan. */
export function isInstanceBacked(type: Pick<SurveyType, 'parts'>): boolean {
  return type.parts.some(isScanPart);
}

/** A type made entirely by hand: parts, none from the scan. */
export function isManualType(type: Pick<SurveyType, 'parts'>): boolean {
  return type.parts.length > 0 && !isInstanceBacked(type);
}

/** `fra scanningen · 91 %` for the classifier's confidence, or null without one. */
export function scanTag(type: Pick<SurveyType, 'confidence' | 'parts'>): Provenance | null {
  if (type.confidence === null || !isInstanceBacked(type)) return null;
  return tag('scan', `fra scanningen · ${Math.round(type.confidence * 100)} %`);
}

/**
 * The tag of a name/materiale value: the classifier's (instance-backed type
 * with a confidence), `indtastet` for a value on a hand-made type, null when
 * empty (the control already reads "Ikke valgt") or unknown.
 */
export function classifiedTag(
  type: Pick<SurveyType, 'confidence' | 'parts' | 'provenance'>,
  value: string | null,
  field?: string,
): Provenance | null {
  if (value === null || value.trim() === '') return null;
  const server = field ? serverTag(type.provenance?.[field]) : null;
  return server ?? scanTag(type) ?? (isManualType(type) ? ENTERED : null);
}

/** Tolerance for "the same quantity" (the prototype rounds a split to one decimal). */
export const EQUAL_TOLERANCE = 0.051;

/** More than one part, all with the same quantity: the type's quantity was spread evenly. */
export function equalSplit(parts: readonly Pick<SurveyPart, 'quantity'>[]): boolean {
  if (parts.length < 2) return false;
  const q0 = parts[0].quantity;
  return parts.every((p) => Math.abs(p.quantity - q0) < EQUAL_TOLERANCE);
}

/**
 * The Mængde tag: `fundet i scanningen` for a stk count of scan parts,
 * `fordelt ligeligt` when the parts share it evenly, `indtastet` for a
 * hand-made type's quantity, `mangler` for none; null for a scan-measured
 * quantity (m², m³) — honest, not guessed.
 */
export function quantityTag(type: Pick<SurveyType, 'unit' | 'quantity' | 'parts' | 'provenance'>): Provenance | null {
  if (type.unit === 'stk' && isInstanceBacked(type)) return FOUND;
  if (!type.quantity) return MISSING;
  if (hasServerProvenance(type)) {
    // The server's word: the type's own entry (a typed total), else what the parts agree on.
    const own = serverTag(type.provenance?.quantity);
    if (own) return own;
    const live = type.parts.filter((p) => !p.dismissed);
    const parts = partsTag(live, 'quantity');
    if (parts) return parts;
    if (live.some((p) => p.provenance?.quantity)) return null;
  }
  if (equalSplit(type.parts)) return SPLIT;
  if (!isInstanceBacked(type)) return ENTERED;
  return null;
}

/** The stk quantity of scan parts is a count, not a field: shown as text. */
export function quantityIsCount(type: Pick<SurveyType, 'unit' | 'parts'>): boolean {
  return type.unit === 'stk' && isInstanceBacked(type);
}

export interface EakTag {
  tag: Provenance | null;
  /** The build-up's code, when it differs from the type's ("Brug 17.01.02"). */
  suggestion: string | null;
  /** The code disagrees with the build-up (warn: "passer ikke med opbygningen"). */
  conflict: boolean;
}

/**
 * The EAK tag: `foreslået` when it is the build-up's main layer code,
 * `mangler` when empty (with the build-up's code offered), a conflict note
 * when the build-up suggests another code, `indtastet` otherwise. A forurenet
 * sample answer suppresses the build-up suggestion (`hazardous`: the code
 * should be a `*` one instead).
 */
export function eakTag(type: Pick<SurveyType, 'eak_code' | 'provenance'>, buildup: Buildup | null, hazardous = false): EakTag {
  const eak = type.eak_code.trim();
  const suggested = buildup?.main.eak ?? null;
  const server = eak ? serverTag(type.provenance?.eak_code) : null;
  if (buildup && eak && eak === suggested) return { tag: server ?? SUGGESTED, suggestion: null, conflict: false };
  if (suggested && !hazardous) {
    return { tag: eak ? null : MISSING, suggestion: suggested, conflict: eak !== '' };
  }
  if (!eak) return { tag: hazardous ? null : MISSING, suggestion: null, conflict: false };
  return { tag: server ?? ENTERED, suggestion: null, conflict: false };
}

/** Within this of the computed value, the tonnes count as `beregnet`. */
export const CALC_TOLERANCE = 0.05;

export type MassSource = 'missing' | 'calculated' | 'entered';

/** Where the tonnes came from, given the build-up's computed value (null: none). */
export function massSource(mass: number | null, calc: number | null): MassSource {
  if (mass === null) return 'missing';
  if (calc !== null && Math.abs(mass - roundTenth(calc)) < CALC_TOLERANCE + 1e-9) return 'calculated';
  return 'entered';
}

/** The Tons tag. */
export function massTag(mass: number | null, calc: number | null): Provenance {
  const src = massSource(mass, calc);
  return src === 'missing' ? MISSING : src === 'calculated' ? CALCULATED : ENTERED;
}

export interface MassView {
  /** The tonnes the row shows: the effective value (override, else calculated). */
  value: number | null;
  /** What the build-up gives, to one decimal; null when nothing can be computed. */
  calc: number | null;
  source: MassSource;
  tag: Provenance;
  /** The row's sub line: how it was computed, or what to do. */
  sub: string | null;
  /**
   * The "Brug beregnet" action: `clear` (PATCH `mass_t: null`, the server
   * drops the override), `set` (PATCH the computed tonnes, an older
   * server), or null when there is nothing to switch to.
   */
  useCalc: { kind: 'clear' | 'set'; tonnes: number } | null;
  /** The server's notes on the calculation (`mass_warnings`). */
  warnings: string[];
}

/** "areal × målt tykkelse × densitet" — the build-up's thickness was replaced by the wall's. */
const MEASURED_THICKNESS = /målt tykkelse/;
/** `measure:thickness median 0.205 m over 3 wall(s)` — the source of a suggested build-up. */
const MEDIAN = /thickness median ([\d.]+) m/;

/**
 * The calculated tag spelled out: `beregnet · b10 beton, 0,205 m målt` (the
 * build-up, its main material, and the wall thickness measured in the model
 * when the calculation used it).
 */
export function calculatedLabel(
  type: Pick<SurveyType, 'mass_method' | 'provenance' | 'buildup_id'>,
  buildup: Buildup | null,
): string {
  const id = buildup?.id ?? type.buildup_id ?? null;
  if (!id) return 'beregnet';
  let label = `beregnet · ${id}${buildup ? ` ${buildup.main.material}` : ''}`;
  if (MEASURED_THICKNESS.test(type.mass_method ?? '')) {
    const median = MEDIAN.exec(type.provenance?.buildup_id?.source ?? '')?.[1];
    label += median ? `, ${formatNumber(Number(median), 3)} m målt` : ', tykkelse målt';
  }
  return label;
}

/**
 * The Tons row. On a server that computes the tonnes (`mass_source` sent)
 * everything comes from it — effective value, calculation, override, method,
 * warnings, and the override's own provenance (an entry of kind `scan`
 * reads "fra scanningen · 84 %"). Otherwise the frontend's calculation
 * (`calc`, from the build-up) is compared with the stored value, as before.
 */
export function massView(
  type: Pick<
    SurveyType,
    'mass_t' | 'mass_calc_t' | 'mass_source' | 'mass_method' | 'mass_warnings' | 'mass_override_t' | 'provenance' | 'buildup_id' | 'unit'
  >,
  buildup: Buildup | null,
  clientCalc: number | null,
  clientMethod: string | null,
): MassView {
  const takes = type.unit !== 'stk';
  if (type.mass_source !== undefined) {
    const calcRaw = type.mass_calc_t ?? null;
    const calc = calcRaw !== null ? roundTenth(calcRaw) : null;
    const warnings = type.mass_warnings ?? [];
    if (type.mass_source === 'override') {
      const t = serverTag(type.provenance?.mass_t);
      return {
        value: type.mass_t,
        calc,
        source: 'entered',
        tag: t && t.kind !== 'calculated' ? t : ENTERED,
        sub: calc !== null && type.mass_method ? `Beregning: ${type.mass_method}` : null,
        useCalc: calc !== null ? { kind: 'clear', tonnes: calc } : null,
        warnings,
      };
    }
    if (type.mass_source === 'calculated') {
      return {
        // Shown to one decimal, as the big figure; the server keeps the exact sum.
        value: calc,
        calc,
        source: 'calculated',
        tag: tag('calculated', calculatedLabel(type, buildup)),
        sub: type.mass_method ? `Beregnet: ${type.mass_method}` : null,
        useCalc: null,
        warnings,
      };
    }
    return {
      value: null,
      calc,
      source: 'missing',
      tag: MISSING,
      sub: takes ? (type.buildup_id ? 'Ingen mængde at regne på endnu — mål fra modellen, eller tast vægten' : 'Vælg en opbygning, eller tast vægten') : null,
      useCalc: null,
      warnings,
    };
  }
  const src = massSource(type.mass_t, clientCalc);
  const rounded = clientCalc !== null ? roundTenth(clientCalc) : null;
  let sub: string | null = null;
  if (src === 'calculated' && clientMethod) sub = `Beregnet: ${clientMethod}`;
  else if (src === 'missing' && !buildup && takes) sub = 'Vælg en opbygning, eller tast vægten';
  return {
    value: type.mass_t,
    calc: rounded,
    source: src,
    tag: massTag(type.mass_t, clientCalc),
    sub,
    useCalc: rounded !== null && src !== 'calculated' ? { kind: 'set', tonnes: rounded } : null,
    warnings: [],
  };
}

/** The tag for a plain value: `indtastet` when set, else nothing (an empty control says enough). */
export function valueTag(value: string | null): Provenance | null {
  return value !== null && value.trim() !== '' ? ENTERED : null;
}
